import { OptionType, ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { AcceptedFiles } from '@insurancemenu/imenu-components-lib/dist/types/FileUpload';
import { getAllDomains } from 'api/operations/domainServiceV2';
import { domainServiceHelper } from 'global/services-helpers';
import { uploadFiles } from 'global/utils/uploadFiles';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { RootStore } from 'store/rootStore';
import { FileType, Upload, UploadFile } from 'types/carrierFileService';
import { Domain } from 'types/domainServiceV2';

export interface ToastInfo {
  showToast: boolean;
  toastType: string;
  toastMessage: string;
}

export default class UploadSyncFilesStore {
  rootStore: RootStore;
  loading = false;
  modalLoading = false;
  fileUploads: Upload[] = [];
  uploadId = '';
  domains: Domain[] = [];
  isWarningModalOpen = false;
  isFileUploadReset = false;
  sponsorId = '';
  date = {
    from: new Date().toISOString().slice(0, 10),
    to: new Date().toISOString().slice(0, 10),
  };

  toastInfo: ToastInfo = {
    showToast: false,
    toastType: 'SUCCESS',
    toastMessage: '',
  };

  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  private setModalLoading(loadingStatus: boolean): void {
    this.modalLoading = loadingStatus;
  }

  public setSponsorId(id: string): void {
    this.sponsorId = id;
  }

  public updateDate(from: string, to: string): void {
    this.date = { from, to };
  }

  private showToast(toastMessage: string, type: string): void {
    this.resetToastInfo();
    this.toastInfo.showToast = true;
    this.toastInfo.toastMessage = toastMessage;
    this.toastInfo.toastType = type;
  }

  public closeToast(): void {
    this.toastInfo.showToast = false;
    this.resetToastInfo();
  }

  private resetToastInfo(): void {
    this.toastInfo.toastType = ToastColorTypes.SUCCESS;
    this.toastInfo.toastMessage = '';
  }

  public setIsFileUploadReset(status: boolean): void {
    this.isFileUploadReset = status;
  }

  public async getFileUploads(from: string, to: string, sponsorId: string): Promise<void> {
    this.setLoading(true);
    const res = await this.rootStore.carrierFileStore.getUploadsByDateRange(from, to, sponsorId);
    runInAction(() => {
      this.fileUploads = res;
      this.setLoading(false);
    });
  }

  public getSponsorOptions(): OptionType[] {
    const convertedDomains = this.domains.map((domain) => toJS(domain)).slice();
    return domainServiceHelper.getSponsorOptions(convertedDomains);
  }

  public closeWarningModal(): void {
    this.isWarningModalOpen = false;
    this.uploadId = '';
  }

  public openWarningModal(uploadId: string): void {
    this.isWarningModalOpen = true;
    this.uploadId = uploadId;
  }

  public async getAllDomains(): Promise<void> {
    const res = await getAllDomains();
    runInAction(() => {
      const domains = res.data.getAllDomains;
      this.domains = domains;
    });
  }

  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-empty-function
  public async deleteUpload(uploadId: string): Promise<void> {
    this.setLoading(true);
    const res = await this.rootStore.carrierFileStore.deleteUpload(uploadId);
    runInAction(() => {
      if (res?.data?.deleteUpload) {
        this.setLoading(false);
      }
    });
  }

  public async validateUpload(uploadId: string): Promise<void> {
    await this.checkUploadStatus(uploadId, 'VALIDATE');
  }

  public async processUpload(uploadId: string): Promise<void> {
    await this.checkUploadStatus(uploadId, 'PROCESS');
  }

  private async checkUploadStatus(uploadId: string, uploadAction: 'VALIDATE' | 'PROCESS'): Promise<void> {
    this.setLoading(true);

    const isValidateAction = uploadAction === 'VALIDATE';
    const request = isValidateAction
      ? this.rootStore.carrierFileStore.validateUpload(uploadId)
      : this.rootStore.carrierFileStore.processUpload(uploadId);

    await request
      .then(() => {
        this.setLoading(false);
        this.showToast(
          isValidateAction ? 'File was successfully validated.' : 'File was successfully processed.',
          ToastColorTypes.SUCCESS,
        );
      })
      .catch(() => {
        this.showToast(isValidateAction ? 'Validation takes too long' : 'Processing  takes too long', 'WARNING');
        this.setLoading(false);
      });
  }

  public async uploadSyncFiles(uploadedFiles: AcceptedFiles, fileType: FileType, sponsorId: string): Promise<void> {
    this.setModalLoading(true);
    const convertedFiles: UploadFile[] = Object.keys(uploadedFiles).map((fileName) => {
      return { type: fileType, name: fileName };
    });
    const res = await this.rootStore.carrierFileStore.getUploadUrls(convertedFiles, sponsorId);
    await runInAction(async () => {
      await uploadFiles(uploadedFiles, res, this.closeWarningModal);
      this.setModalLoading(false);
      this.setIsFileUploadReset(true);
    });
  }

  public async getOutputDownloadUrl(uploadId: string, fileName: string): Promise<string> {
    return this.rootStore.carrierFileStore.getOutputDownloadUrl(uploadId, fileName);
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
